import { useMemo } from "react";
import { PieChart, Pie, Cell } from "recharts";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { formatNumber } from "../../utils";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useCompact } from "../../utils/hooks/useCompact";

interface SalePieChartProps {
  cashsaleTotalAmount: number;
  invoiceTotalAmount: number;
  /** Hide the built-in bottom legend (e.g. when the card renders its own). */
  hideLegend?: boolean;
}

const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  index,
  pieChartData,
  fill,
}: any) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  const value = pieChartData[index].value;
  const percentText = `(${Math.round(percent * 100)}%)`;
  const text = `฿${
    value >= 1000
      ? `${formatNumber(value / 1000, { useGrouping: false })}K`
      : formatNumber(value)
  }`;

  return (
    <text
      x={x}
      y={y - 10}
      fill={fill}
      textAnchor="middle"
      fontWeight={600}
      fontSize={18}
    >
      {text}
      <tspan x={x} y={y + 18} fontSize={16} fontWeight={400} fill={fill}>
        {percentText}
      </tspan>
    </text>
  );
};

export default function SalePieChart({
  invoiceTotalAmount,
  cashsaleTotalAmount,
  hideLegend = false,
}: SalePieChartProps) {
  const theme = useTheme();
  const isCompact = useCompact();
  // In the 7/5 layout (>1280px) the right column is still tight up to ~1453px,
  // so densify the pie/legend there as well to keep them inside the column.
  const isTight = useMediaQuery(
    "(min-width:1281px) and (max-width:1453px)",
    { noSsr: true },
  );
  const dense = isCompact || isTight;
  const labelColor = theme.palette.text.primary;

  const pieSize = dense ? 200 : 240;
  const pieInnerRadius = dense ? 42 : 50;
  const pieOuterRadius = dense ? 100 : 120;
  const zeroDotCx = pieSize / 2;
  const zeroDotCy = dense ? 26 : 30;
  const emptyOuterSize = dense ? 200 : 240;
  const emptyInnerSize = dense ? 166 : 200;

  const pieChartData = useMemo(() => {
    return [
      { name: "เงินสด", value: cashsaleTotalAmount, color: "#6066ea" },
      { name: "เงินเชื่อ", value: invoiceTotalAmount, color: "#73ceea" },
    ];
  }, [cashsaleTotalAmount, invoiceTotalAmount]);

  const isCashZero = cashsaleTotalAmount === 0 && invoiceTotalAmount > 0;
  const isInvoiceZero = invoiceTotalAmount === 0 && cashsaleTotalAmount > 0;
  const isAllZero = cashsaleTotalAmount === 0 && invoiceTotalAmount === 0;

  return (
    <Box
      sx={{
        flex: 1.2,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        margin: "auto",
      }}
    >
      {!isAllZero && (
        <PieChart width={pieSize} height={pieSize}>
          <Pie
            data={pieChartData}
            dataKey="value"
            innerRadius={pieInnerRadius}
            outerRadius={pieOuterRadius}
            startAngle={90}
            endAngle={-270}
            stroke="none"
            label={(props) =>
              renderCustomizedLabel({
                ...props,
                pieChartData,
                fill: labelColor,
              })
            }
            labelLine={false}
          >
            {pieChartData.map((entry) => (
              <Cell key={entry.name} fill={entry.color} />
            ))}
          </Pie>

          {isCashZero && (
            <circle cx={zeroDotCx} cy={zeroDotCy} r={5} fill="#6066ea" />
          )}
          {isInvoiceZero && (
            <circle cx={zeroDotCx} cy={zeroDotCy} r={5} fill="#73ceea" />
          )}
        </PieChart>
      )}
      {isAllZero && (
        <Box
          sx={{
            position: "relative",
            width: emptyOuterSize,
            height: emptyOuterSize,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              width: emptyInnerSize,
              height: emptyInnerSize,
              borderRadius: "50%",
              border: "1px solid rgba(0, 0, 0, 0.08)",
              background:
                "conic-gradient(rgba(96,102,234,0.1) 0deg 180deg, rgba(115,206,234,0.1) 180deg 360deg)",
            }}
          />
          <Typography
            color="text.secondary"
            fontWeight={500}
            sx={{
              position: "relative",
              zIndex: 1,
              textAlign: "center",
            }}
          >
            ไม่พบข้อมูล
          </Typography>
        </Box>
      )}

      {!hideLegend && (
        <Box
          display="flex"
          gap={dense ? 3 : 6}
          justifyContent="center"
          alignItems="center"
          sx={{ mt: dense ? 1 : 2 }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 24,
                height: 24,
                flexShrink: 0,
                borderRadius: "50%",
                bgcolor: "#73ceea",
                mr: 1,
              }}
            />
            <Typography fontWeight={600}>เงินเชื่อ</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 24,
                height: 24,
                flexShrink: 0,
                borderRadius: "50%",
                bgcolor: "#6066ea",
                mr: 1,
              }}
            />
            <Typography fontWeight={600}>เงินสด</Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}
